/**
 * JobsTable - Reusable job table component
 *
 * Design: Parent provides data, component handles presentation
 * Pattern: Composition over configuration (React 19)
 * Complexity: Target 8-10 cyclomatic (thin wrapper)
 *
 * Used by:
 * - CapabilityJobStatus (capability drawer modal)
 * - Future: RiskDrawer, AssetDrawer jobs tabs
 *
 * @example
 * ```tsx
 * <JobsTable
 *   name="capability-jobs"
 *   jobs={jobs}
 *   status={isLoading ? 'pending' : 'success'}
 *   columns={[
 *     jobColumnFactories.status(),
 *     jobColumnFactories.source(),
 *     jobColumnFactories.updated(),
 *   ]}
 *   rowActions={job => ({
 *     menu: { items: [{ label: 'Rerun', onClick: () => rerun(job.key) }] }
 *   })}
 * />
 * ```
 */

import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { ReactNode } from 'react';

import { Table } from '@/components/table/Table';
import { Column, TableActions } from '@/components/table/types';
import { createStandardJobColumns } from '@/sections/jobs/config/columnFactories';
import { Job } from '@/types';

/**
 * Props for JobsTable component
 */
export interface JobsTableProps {
  /** Table identity for storage keys */
  name: string;

  /** Job data - parent fetches and provides */
  jobs: Job[];

  /** Loading/error status */
  status: 'pending' | 'success' | 'error';

  /** Optional error object */
  error?: Error | null;

  /** Column configuration - defaults to standard job columns */
  columns?: Column<Job>[];

  /** Enable row selection with checkboxes */
  enableSelection?: boolean;

  /** Current selected row keys */
  selectedRows?: string[];

  /** Callback when selection changes */
  onSelectionChange?: (keys: string[]) => void;

  /** Row-level actions (menu items per row) */
  rowActions?: (job: Job) => TableActions;

  /** Empty state configuration */
  emptyState?: {
    icon?: ReactNode;
    title: string;
    description: string;
  };

  /** Row click handler */
  onRowClick?: (job: Job, index: number) => void;

  /** Additional className */
  className?: string;

  /** Optional sort configuration - enables column header sorting */
  sort?: {
    orderBy: keyof Job;
    descending: boolean;
    onSort: (newSort: { orderBy: keyof Job; descending: boolean }) => void;
  };

  /** Maximum height for the table scroll container in pixels */
  maxHeight?: number;

  /** Minimum height for the table scroll container in pixels */
  minHeight?: number;

  /** Disable responsive column hiding/compression (default: true) */
  responsive?: boolean;
}

/**
 * Default empty state configuration
 */
const DEFAULT_EMPTY_STATE = {
  icon: <InformationCircleIcon className="text-chariot-stroke size-16" />,
  title: 'No Jobs Found',
  description: 'No jobs match the current filters.',
};

/**
 * JobsTable component
 *
 * Thin wrapper around Table component for job-specific rendering.
 * Follows composition pattern - parent provides data and handles mutations.
 */
export function JobsTable({
  name,
  jobs,
  status,
  error,
  columns,
  enableSelection = false,
  selectedRows = [],
  onSelectionChange,
  rowActions,
  emptyState = DEFAULT_EMPTY_STATE,
  onRowClick,
  className,
  sort,
  maxHeight,
  minHeight,
  responsive,
}: JobsTableProps) {
  // Use provided columns or default to standard job columns
  const tableColumns =
    columns ?? createStandardJobColumns({ animateRunning: true });

  return (
    <div className={className}>
      <Table
        name={name}
        columns={tableColumns}
        data={jobs}
        status={status}
        error={error ?? null}
        noData={emptyState}
        selection={
          enableSelection
            ? {
                value: selectedRows,
                onChange: onSelectionChange,
              }
            : { hide: true }
        }
        rowActions={rowActions}
        onRowClick={onRowClick}
        sort={sort}
        maxHeight={maxHeight}
        minHeight={minHeight}
        responsive={responsive}
      />
    </div>
  );
}
